import { EVENT_TYPES } from './event-types'

const HEX_RE = /^#[0-9a-fA-F]{6}$/

// Fallback for an invalid/missing accent color. True dominant-color sampling
// from the uploaded logo is deferred (would need a new image-processing
// dependency) — a church without a chosen accent gets this neutral default
// until they pick one from the admin swatches.
export const DEFAULT_ACCENT_COLOR = '#485874'

type Rgb = [number, number, number]
type Hsl = [number, number, number] // h in [0,360), s/l in [0,1]

function hexToRgb(hex: string): Rgb {
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v))
}

function rgbToHex([r, g, b]: Rgb): string {
  return (
    '#' +
    [r, g, b]
      .map((v) =>
        Math.round(clamp(v, 0, 255))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  )
}

function rgbToHsl([r, g, b]: Rgb): Hsl {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  switch (max) {
    case rn:
      h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60
      break
    case gn:
      h = ((bn - rn) / d + 2) * 60
      break
    default:
      h = ((rn - gn) / d + 4) * 60
  }
  return [h, s, l]
}

function hueToRgbChannel(p: number, q: number, t: number): number {
  let tt = t
  if (tt < 0) tt += 1
  if (tt > 1) tt -= 1
  if (tt < 1 / 6) return p + (q - p) * 6 * tt
  if (tt < 1 / 2) return q
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
  return p
}

function hslToRgb([h, s, l]: Hsl): Rgb {
  if (s === 0) {
    const v = l * 255
    return [v, v, v]
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hn = h / 360
  return [
    hueToRgbChannel(p, q, hn + 1 / 3) * 255,
    hueToRgbChannel(p, q, hn) * 255,
    hueToRgbChannel(p, q, hn - 1 / 3) * 255,
  ]
}

// WCAG relative luminance.
function relativeLuminance([r, g, b]: Rgb): number {
  const [rl, gl, bl] = [r, g, b].map((v) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl
}

/** WCAG contrast ratio between two hex colors, from 1 (no contrast) to 21. */
export function contrastRatio(hexA: string, hexB: string): number {
  const la = relativeLuminance(hexToRgb(hexA))
  const lb = relativeLuminance(hexToRgb(hexB))
  const [lighter, darker] = la > lb ? [la, lb] : [lb, la]
  return (lighter + 0.05) / (darker + 0.05)
}

// ─── CIE Lab distance (for the event-type-color collision check) ──────────────

function srgbToLinear(c: number): number {
  const v = c / 255
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
}

function rgbToXyz([r, g, b]: Rgb): [number, number, number] {
  const [rl, gl, bl] = [srgbToLinear(r), srgbToLinear(g), srgbToLinear(b)]
  return [
    rl * 0.4124 + gl * 0.3576 + bl * 0.1805,
    rl * 0.2126 + gl * 0.7152 + bl * 0.0722,
    rl * 0.0193 + gl * 0.1192 + bl * 0.9505,
  ]
}

// D65 reference white.
const XN = 0.95047
const YN = 1.0
const ZN = 1.08883

function fLab(t: number): number {
  return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116
}

function xyzToLab([x, y, z]: [number, number, number]): [number, number, number] {
  const fx = fLab(x / XN)
  const fy = fLab(y / YN)
  const fz = fLab(z / ZN)
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)]
}

function hexToLab(hex: string): [number, number, number] {
  return xyzToLab(rgbToXyz(hexToRgb(hex)))
}

/** CIE76 Delta E — perceptual color distance, roughly 0 (identical) to 100. */
export function deltaE(hexA: string, hexB: string): number {
  const [l1, a1, b1] = hexToLab(hexA)
  const [l2, a2, b2] = hexToLab(hexB)
  return Math.sqrt((l1 - l2) ** 2 + (a1 - a2) ** 2 + (b1 - b2) ** 2)
}

const EVENT_TYPE_COLORS = Object.values(EVENT_TYPES).map((t) => t.color)
const COLLISION_DELTA_E = 10
const MIN_CONTRAST_ON_WHITE = 4.5

function collidesWithEventTypeColor(hex: string): boolean {
  return EVENT_TYPE_COLORS.some((c) => deltaE(hex, c) < COLLISION_DELTA_E)
}

/**
 * Darken `hex` (in HSL lightness steps, hue/saturation untouched) until it
 * reaches at least `minRatio` contrast against white. A host band/button text
 * sits directly on white or near-white, so this is the surface that matters.
 */
function darkenForContrast(hex: string, minRatio = MIN_CONTRAST_ON_WHITE): string {
  const hsl = rgbToHsl(hexToRgb(hex))
  const [h, s] = hsl
  let l = hsl[2]
  let candidate = hex
  for (let i = 0; i < 100 && contrastRatio(candidate, '#ffffff') < minRatio && l > 0; i++) {
    l = clamp(l - 0.02, 0, 1)
    candidate = rgbToHex(hslToRgb([h, s, l]))
  }
  return candidate
}

/**
 * Rotate hue in fixed steps until the color no longer reads as an event-type
 * color (ΔE ≥ 10 from every EVENT_TYPES swatch). The host band/pill must never
 * be mistaken for an event-type tint.
 */
function nudgeAwayFromEventTypeColors(hex: string): string {
  if (!collidesWithEventTypeColor(hex)) return hex
  const hsl = rgbToHsl(hexToRgb(hex))
  const [s, l] = [hsl[1], hsl[2]]
  let h = hsl[0]
  for (let i = 0; i < 18; i++) {
    h = (h + 20) % 360
    const candidate = rgbToHex(hslToRgb([h, s, l]))
    if (!collidesWithEventTypeColor(candidate)) return candidate
  }
  return hex // exhausted the hue wheel without escaping collision — return as-is
}

/**
 * Normalize a church-submitted accent color into one safe to store and render
 * everywhere: valid 6-digit hex, readable as text on white (≥4.5:1), and never
 * mistakable for an event-type pill color. Apply once at write time — see §7 of
 * the design handoff ("store one hex; derive tints in CSS via color-mix").
 */
export function resolveAccentColor(hex: string): string {
  const input = HEX_RE.test(hex) ? hex : DEFAULT_ACCENT_COLOR
  return nudgeAwayFromEventTypeColors(darkenForContrast(input))
}
